import type { SnapshotDrillId, SnapshotScoreEntry } from "@healthos/shared";
import { ArrowRight } from "@phosphor-icons/react";
import { BehaviourIcon } from "./behaviour-icon";
import { renderInlineMarkdown } from "./inline-markdown";
import type { SnapshotBehaviourMeta } from "./snapshot-tokens";

/** One finished row. */
export interface PillarListItem {
  /** The visual token for the behaviour: its icon and its hue. */
  behaviour: SnapshotBehaviourMeta;
  score: SnapshotScoreEntry;
  /** No reading, or one the member said to discount. */
  quiet: boolean;
  /** The figure in the right-hand column, already worded for the quiet cases. */
  value: string;
  /** One plain-English line for the week. */
  line: string;
  /** The drill-in that grades this behaviour, or null when the row is not a door. */
  drill: SnapshotDrillId | null;
  focus: boolean;
  /** True when the member's current goal is on this behaviour. */
  goal: boolean;
  /** The level this behaviour is practising, when it has one. */
  level: number | null;
  /** What Heartbeat weighs for this behaviour, when it weighs anything. */
  carrying: string | null;
}

// The overview rows: dot, name, one line, bar, value. A row with a drill-in is
// the door to it, never the destination.
export function PillarList({
  items,
  onOpen,
}: {
  items: ReadonlyArray<PillarListItem>;
  onOpen: (drill: SnapshotDrillId) => void;
}) {
  return (
    <ul
      className="snap-pl-list"
      aria-label="Five behaviours this week. Select one to open its readings."
    >
      {items.map(
        ({ behaviour, score, quiet, value, line, drill, focus, goal, level, carrying }) => {
          return (
            <li
              key={behaviour.id}
              className={`snap-pl${quiet ? " is-quiet" : ""}${focus ? " is-focus" : ""}${drill ? "" : " is-nowhere"}`}
              style={{ ["--accent" as string]: behaviour.color }}
            >
              <button
                type="button"
                disabled={drill === null}
                onClick={drill ? () => onOpen(drill) : undefined}
              >
                <span className="snap-pl-dot">
                  <BehaviourIcon name={behaviour.iconName} size={13} weight="duotone" />
                </span>
                <span className="snap-pl-k">
                  {behaviour.label}
                  {goal ? <em className="snap-pl-tag">goal</em> : null}
                </span>
                <span className="snap-pl-say">
                  {renderInlineMarkdown(line)}
                  {carrying ? <em className="snap-pl-carry">{carrying}</em> : null}
                </span>
                <i className={`snap-pl-lv${level === null ? " is-empty" : ""}`}>
                  {level === null ? "" : `Lv ${level}`}
                </i>
                <span className="snap-pl-bar">
                  {quiet ? null : <i style={{ width: `${score.score}%` }} />}
                </span>
                <span className="snap-pl-v">{value}</span>
                {drill ? <ArrowRight size={13} className="snap-pl-go" aria-hidden="true" /> : null}
              </button>
            </li>
          );
        },
      )}
    </ul>
  );
}
